import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    const el =
      this.querySelector('.header-actions__cart-bubble [ref="cartBubbleCount"], .header-actions__cart-bubble .cart-bubble__text-count') ??
      this.refs.cartBubbleCount;
    return parseInt(el?.textContent ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    const label = value < 100 ? String(value) : '';
    // Header renders two cart-bubble trees (text + icon) with the same ref —
    // Component refs bind only one node, so update every matching count.
    this.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__text-count').forEach((el) => {
      el.textContent = label;
    });
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Product-form sends a delta; cart-items / other sources send absolute counts.
    const nextCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.currentCartCount = nextCount;

    this.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__text-count').forEach((el) => {
      el.classList.toggle('hidden', nextCount === 0);
    });
    this.querySelectorAll('[ref="cartBubble"], .cart-bubble').forEach((el) => {
      el.classList.toggle('visually-hidden', nextCount === 0);
    });

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(nextCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || nextCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const bubbles = this.querySelectorAll('[ref="cartBubble"], .cart-bubble');
    bubbles.forEach((el) => el.classList.add('cart-bubble--animating'));
    const textEl = this.refs.cartBubbleText ?? this.querySelector('[ref="cartBubbleText"], .cart-bubble__text');
    if (textEl) await onAnimationEnd(textEl);
    bubbles.forEach((el) => el.classList.remove('cart-bubble--animating'));
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleEl =
      this.querySelector('.header-actions__cart-bubble [ref="cartBubbleCount"], .header-actions__cart-bubble .cart-bubble__text-count') ??
      this.refs.cartBubbleCount;
    if (!visibleEl) return;

    const visibleCount = visibleEl.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
